import { useState } from "react";
import { CheckCircle2, AlertCircle, ArrowRight, Phone, Ticket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import type { Wizard } from "@/lib/supportContent";
import { SUPPORT_PHONE, SUPPORT_PHONE_TEL } from "@/lib/supportContent";

export const TroubleshootWizard = ({ wizard }: { wizard: Wizard }) => {
  const [step, setStep] = useState(0);
  const [resolved, setResolved] = useState(false);
  const exhausted = step >= wizard.steps.length;

  if (resolved) {
    return (
      <Card className="border-[#00F5A0]/40 bg-[#00F5A0]/5">
        <CardContent className="p-8 text-center space-y-4">
          <CheckCircle2 className="w-12 h-12 text-[#00F5A0] mx-auto" />
          <h3 className="text-xl font-bold">Glad we got that sorted!</h3>
          <p className="text-muted-foreground">If the issue comes back, open a ticket so we have a record.</p>
          <Button asChild variant="outline"><Link to="/portal/support?new=ticket"><Ticket className="w-4 h-4 mr-2" />Log a Ticket (Optional)</Link></Button>
        </CardContent>
      </Card>
    );
  }

  if (exhausted) {
    return (
      <Card className="border-[#FFD700]/40 bg-[#FFD700]/5">
        <CardContent className="p-8 space-y-4">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-8 h-8 text-[#FFD700]" />
            <h3 className="text-xl font-bold">Time to bring in our team</h3>
          </div>
          <p className="text-muted-foreground">{wizard.escalate}</p>
          <div className="flex flex-wrap gap-3 pt-2">
            <Button asChild className="bg-gradient-to-r from-[#00F5A0] to-[#00D9F5] text-[#05070A] font-semibold">
              <a href={SUPPORT_PHONE_TEL}><Phone className="w-4 h-4 mr-2" />Call {SUPPORT_PHONE}</a>
            </Button>
            <Button asChild variant="outline">
              <Link to={`/portal/support?new=ticket&context=${encodeURIComponent(`${wizard.robot}: ${wizard.issue}`)}`}><Ticket className="w-4 h-4 mr-2" />Open a Ticket</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const current = wizard.steps[step];
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
          <span>Step {step + 1} of {wizard.steps.length}</span>
          <span>{wizard.robot}</span>
        </div>
        <CardTitle className="text-xl">{current.title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="text-muted-foreground leading-relaxed">{current.body}</p>
        <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#00F5A0] to-[#00D9F5] transition-all" style={{ width: `${((step + 1) / wizard.steps.length) * 100}%` }} />
        </div>
        <div className="flex flex-wrap gap-3 pt-2">
          <Button onClick={() => setResolved(true)} className="bg-[#00F5A0] text-[#05070A] hover:bg-[#00F5A0]/90">
            <CheckCircle2 className="w-4 h-4 mr-2" />This fixed it
          </Button>
          <Button onClick={() => setStep(step + 1)} variant="outline">
            Still broken — next step <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
